import * as React from 'react'
import {
  useGetAuthzMeQuery,
  useGetTabularIngestJobDetailQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { COMPACT_ACTION, COMPACT_PAGE, META_TEXT } from '../IngestPage/shared/compact'
import {
  PagePane,
  pagesInFilter,
  useLeaveGuard,
  usePageSteps,
  type PageFilter,
  type RunPage
} from './PagesWorkspace'
import { tabularJobPagePath, tabularJobPath } from './route'
import { useRunPageReview } from './useRunPageReview'

const NO_PAGES: RunPage[] = []

/** One page of an agent run, full width, stepping through the run's pages with J / K. */
export default function TabularRunPagePage() {
  const { jobId = '', pageId = '' } = useParams<{ jobId: string; pageId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const detailQuery = useGetTabularIngestJobDetailQuery(jobId, { skip: !jobId })
  const detail = detailQuery.data
  const pages = detail?.pages ?? NO_PAGES
  const canManage = useGetAuthzMeQuery().data?.permissions.includes('ingest/manage') === true
  const review = useRunPageReview(detail, canManage)
  const decisions = review?.decisions
  // A page outside the requested filter (reached by link) steps through every page instead.
  const filter: PageFilter =
    searchParams.get('filter') === 'todo' &&
    pagesInFilter(pages, 'todo', decisions).some((page) => page.pageId === pageId)
      ? 'todo'
      : 'all'
  const order = React.useMemo(
    () => pagesInFilter(pages, filter, decisions),
    [pages, filter, decisions]
  )
  const step = React.useCallback(
    (next: string) =>
      navigate(tabularJobPagePath(jobId, next, filter === 'todo' ? 'todo' : undefined), {
        replace: true,
        state: location.state
      }),
    [filter, jobId, location.state, navigate]
  )
  const go = useLeaveGuard(review, pageId, step)
  usePageSteps(order, pageId, go)

  if (detailQuery.isLoading || !detail) {
    return (
      <div className="mx-auto w-full max-w-6xl py-8 text-xs text-ink-3">
        {detailQuery.isError ? 'Tabular ingest job was not found.' : 'Loading page…'}
      </div>
    )
  }
  const runPath = tabularJobPath(jobId, { tab: 'pages', page: pageId })
  const page = pages.find((candidate) => candidate.pageId === pageId)
  const at = order.findIndex((candidate) => candidate.pageId === pageId)
  const previous = order[at - 1]
  const next = order[at + 1]
  return (
    <div className={COMPACT_PAGE}>
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-6 w-fit px-1.5 text-11">
          <Link
            to={runPath}
            state={location.state}
            onClick={(event) => {
              if (review && !review.edits.confirmLeave()) event.preventDefault()
            }}
          >
            <ArrowLeft className="size-3" />
            Back to run
          </Link>
        </Button>
        <span className={META_TEXT}>
          {detail.configName} v{detail.configVersion}
          {page ? ` · ${page.fileName} · page ${page.pageNumber}` : ''}
        </span>
      </nav>
      {!page ? (
        <p className="text-xs text-ink-3">This run has no such page.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className={COMPACT_ACTION}
              disabled={!previous}
              onClick={() => previous && go(previous.pageId)}
            >
              <ChevronLeft className="size-3" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={COMPACT_ACTION}
              disabled={!next}
              onClick={() => next && go(next.pageId)}
            >
              Next
              <ChevronRight className="size-3" />
            </Button>
            <span className={META_TEXT}>
              {at + 1} of {order.length} {filter === 'todo' ? 'to review' : 'pages'} · J / K step
              through them
            </span>
          </div>
          <PagePane jobId={jobId} page={page} pages={pages} review={review} onSelect={go} />
        </>
      )}
    </div>
  )
}
